import { ProductTypesEnum } from "#/constants";
import { ItemQuantityCounter } from "#/pages/cart/components/counter";
import { cart } from "#/utils";
import IRRtoIRT from "#/utils/currency/IRRtoIRT";
import { getSubscriptionLabel } from "#/utils/text/getSubscriptionLabel";
import { Icon, Toast } from "@monta/design-system";
import { MontaLink, useMontaNavigate } from "@monta/router-react";
import type { TAddToCart, TCartDetailedItem } from "@monta/utils";
import { useEffect, useState } from "react";
import TagManager from "react-gtm-module";
import styles from "./cartItem.module.scss";

export interface CartItemProps {
  item: TCartDetailedItem;
  onRemove: (item?: TCartDetailedItem) => void;
}

const CartItem = (props: CartItemProps) => {
  const { item, onRemove } = props;
  const [shouldRecallApi, setShouldRecallApi] = useState<boolean>(false);
  const [itemCount, setItemCount] = useState<number>();
  const navigate = useMontaNavigate();

  const {
    productName,
    productTypeId,
    productId,
    coverImage,
    priceAfterDiscount,
    priceBeforeDiscount,
    discountPercent,
    count,
    credit,
    period,
    productPackageId,
    targetGroupId,
    productPackageDetails,
    limitationId,
    buyerTypeId,
    secondaryInfo,
    refId,
  } = item;

  const counterBasedProducts = [
    ProductTypesEnum.BANKB2C.valueOf(),
    ProductTypesEnum.EXAM_MARKET.valueOf(),
    ProductTypesEnum.ONLINE_EXAM.valueOf(),
    ProductTypesEnum.OFFLINE_EXAM.valueOf(),
    ProductTypesEnum.AUTOMATION.valueOf(),
    ProductTypesEnum.EDUCATION_PACK.valueOf(),
  ];
  const hasCounter =
    counterBasedProducts.includes(productTypeId) && buyerTypeId !== 1;

  useEffect(() => {
    setItemCount(count);
  }, [count]);

  const isBundle = productPackageDetails.length > 0;

  const handleAddToCart = () => {
    let packageProductModel: TAddToCart;
    if (productPackageDetails.length > 0) {
      packageProductModel = {
        productPackageId: productPackageId,
        targetGroupId: targetGroupId,
        productPackageDetails,
        count: itemCount as number,
        credit,
      };
    } else {
      packageProductModel = {
        productId,
        productPackageId: productPackageId,
        targetGroupId: targetGroupId,
        limitationId: limitationId,
        count: itemCount as number,
        credit,
      };
    }
    cart
      .add(packageProductModel)
      .then(() => {
        const tagManagerArgs = {
          dataLayer: {
            event: "add_to_cart",
            ecommerce: {
              value: priceAfterDiscount,
              tax: 13.5,
              shipping: 0,
              currency: "USD",
              coupon: "",
              items: [
                {
                  item_id: packageProductModel.productId,
                  item_name: productName,
                  quantity: 1,
                  coupon: "",
                  discount: discountPercent,
                  item_brand: "MONTA",
                  item_category: "",
                  price: priceAfterDiscount,
                },
              ],
            },
          },
        };
        TagManager.dataLayer(tagManagerArgs);
        Toast.success({
          message: `${productName} به تعداد ${itemCount} عدد به سبد خرید اضافه شد.`,
        });
      })
      .catch(() => Toast.error({ message: "خطایی رخ داده است." }));
  };

  useEffect(() => {
    if (shouldRecallApi) {
      const handler = setTimeout(() => handleAddToCart(), 1500);

      return () => {
        clearTimeout(handler);
      };
    }
  }, [itemCount, shouldRecallApi]);

  const handleRemoveFromCart = async (id: number) => {
    await cart.remove({ productId: null, productPackageId: id }).then(() => {
      const tagText =
        "حذف از سبد خرید" +
        "-" +
        {
          [ProductTypesEnum.BANKB2B]: "بانک سوال",
          [ProductTypesEnum.BANKB2C]: "بانک سوال ویژه دانش آموز",
          [ProductTypesEnum.EXAM_MARKET]: "اگزم مارکت",
          [ProductTypesEnum.EDUCATION_PACK]: "بسته آموزشی ویژه دانش آموز",
        }[Number(productTypeId)] +
        "-" +
        `${productPackageDetails ? "بسته ترکیبی" : "تک محصول"}` +
        "-" +
        productName;
      const tagManagerArgs = {
        dataLayer: {
          event: "product-remove-from-cart",
          value: { name: tagText },
        },
      };
      TagManager.dataLayer(tagManagerArgs);
      Toast.success({ message: "این محصول با موفقیت از سبد خرید حذف شد." });
    });
  };

  const handleCountChange = (itemId: number, countNum: number) => {
    setItemCount(countNum);
    if (countNum) {
      setShouldRecallApi(true);
    } else {
      handleRemoveFromCart(itemId);
      setShouldRecallApi(false);
    }
  };

  return (
    <div className={styles["main-container"]}>
      <div className={styles["item-container"]}>
        {hasCounter && itemCount ? (
          <ItemQuantityCounter
            count={itemCount}
            onCountChange={(number) =>
              handleCountChange(productPackageId, number)
            }
            variant="secondary"
            maxCount={999}
          />
        ) : (
          <button
            onClick={() => onRemove(item)}
            className={styles["item-container__delete"]}
          >
            <Icon
              icon="mi-Delete-Linear"
              size={1.3}
              className={styles["item-container__price__delete__text"]}
            />
          </button>
        )}

        <div className={styles["item-container__image-container"]}>
          <img
            src={coverImage}
            alt={productName}
            onClick={() => {
              navigate(
                `/product/${
                  isBundle ? "pkg-" : "prd-"
                }${refId}?buyer-type-id=${buyerTypeId}&period=${period}`,
                { baseUrl: "LANDING_URL" },
              );
            }}
          />

          {discountPercent !== 0 && (
            <div
              className={styles["item-container__image-container--discount"]}
            >
              {discountPercent}%
            </div>
          )}
        </div>
        <div>
          <MontaLink
            baseURL="LANDING_URL"
            to={`/product/${
              isBundle ? "pkg-" : "prd-"
            }${refId}?buyer-type-id=${buyerTypeId}&period=${period}`}
            className={styles["item-container__title"]}
          >
            {productName}
          </MontaLink>
          {secondaryInfo && (
            <h3 className={styles["item-container__info"]}>{secondaryInfo}</h3>
          )}
          {item.period && (
            <div className={styles["item-container__period"]}>
              <label className={styles["item-container__period-title"]}>
                مدت زمان:
              </label>
              <span className={styles["item-container__period-value"]}>
                {getSubscriptionLabel(item.period)}
              </span>
            </div>
          )}
          <div className={styles["item-container__price"]}>
            <div className={styles["item-container__price-container"]}>
              {discountPercent !== 0 && (
                <span className={styles["item-container__price--discount"]}>
                  {IRRtoIRT(priceBeforeDiscount).toLocaleString()}
                </span>
              )}
              <div className={styles["item-container__price-container"]}>
                <span className={styles["item-container__item-value"]}>
                  <span className={styles["item-container__item-value--price"]}>
                    {IRRtoIRT(priceBeforeDiscount).toLocaleString()}
                  </span>
                  <span
                    className={
                      styles["item-container__item-value--priceMobile"]
                    }
                  >
                    {IRRtoIRT(
                      discountPercent !== 0
                        ? priceAfterDiscount
                        : priceBeforeDiscount,
                    ).toLocaleString()}
                  </span>
                  <span>تومان</span>
                </span>
              </div>
              {discountPercent !== 0 && (
                <div className={styles["item-container__item-discount"]}>
                  <div
                    className={
                      styles["item-container__item-discount--separator"]
                    }
                  ></div>
                  <span
                    className={styles["item-container__item-discount--benefit"]}
                  >
                    {IRRtoIRT(
                      priceBeforeDiscount - priceAfterDiscount,
                    ).toLocaleString()}{" "}
                    تومان
                  </span>
                  <span
                    className={styles["item-container__item-discount--text"]}
                  >
                    سود شما از خرید
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartItem;
